/**
 * DecisionBar — sticky footer with Next-Step / Review / Discard actions.
 *
 * S-072 / FEAT-006 (Phase 9). Swapped from Directus-backed
 * `useUpdateArticle` to a Hono PATCH /pieces/:id mutation. Status flips
 * derive the new ui_state client-side (mirroring the legacy
 * `useUpdateArticle` hook's auto-derive behavior) so the kanban groups
 * the piece correctly without waiting for a server-side recompute.
 *
 * The component consumes the legacy snake_case PieceDetail shape (still
 * keyed `article` on the FK columns for adapter parity), which PieceView
 * adapts from the Hono PieceRow via `piece-adapter.ts`.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi } from "@/lib/api";
import { decisionLanded, describeRunError } from "@/lib/run-errors";
import { isStoppable, nextStepFor } from "@/lib/ui-state";
import { useRole } from "@/lib/useRole";
import type { PieceDetail, UiState } from "@/types/piece";
import { isFictionArticle } from "@shared/fiction-phases";
import { Icon } from "./Icon";
import { RevisionModal, completedPhaseIndex } from "./RevisionModal";
import { SendForReviewModal } from "./SendForReviewModal";

interface Props {
  piece: PieceDetail;
  /** When true, hide the Next Step button — a custom selector handles approval instead. */
  suppressNextStep?: boolean;
  /** FEAT-058 — current draft HTML, for the "Send for feedback" mailto excerpt. Null disables the button (no draft yet). */
  draftHtml?: string | null;
}

/**
 * FEAT-108 / S-438 — one call per decision. Approve and Request revision used
 * to PATCH the status and then POST /run, which was admin-only: an Editor's or
 * Reviewer's decision changed the status, got a 403, and the piece stalled.
 * The server now works out the step and continues the pipeline itself.
 */
type Decision =
  | { action: "approve" }
  | { action: "reject" }
  | { action: "revise"; revision_target: string; revision_notes: string };

function useDecision(pieceId: number) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (decision: Decision) =>
      api(`/pieces/${pieceId}/decision`, {
        method: "POST",
        body: JSON.stringify(decision),
      }),
    // Settled, not success: a run refused after the decision landed (no key,
    // queue down) still changed the piece, and the bar must show that.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
  });
}

export function DecisionBar({ piece, suppressNextStep = false, draftHtml = null }: Props) {
  const [showRevision, setShowRevision] = useState(false);
  const [showSendForReview, setShowSendForReview] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  // BUG-042 — tracks the full patch-then-run sequence (update.isPending only
  // covers the PATCH half), so the button stays disabled/labeled through the
  // whole click-to-navigate flow, not just the first network call.
  const [nextStepPending, setNextStepPending] = useState(false);
  const [nextStepError, setNextStepError] = useState<string | null>(null);
  const update = useDecision(piece.id);
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // FEAT-038 — Stop is an admin-only recovery action. Gate it here (rather than
  // relying on the parent view) so it can never render for a non-admin.
  // FEAT-108 — the decisions themselves are open to Admin, Editor and Reviewer.
  const { isAdmin, canDecide } = useRole();

  const stop = useMutation({
    mutationFn: async () => api(`/pieces/${piece.id}/stop`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece", piece.id] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
      setConfirmStop(false);
    },
  });
  const showStop = isAdmin && isStoppable(piece);
  // A piece with nothing completed yet has no stage to send "back" to.
  // Non-fiction only: RevisionModal's stage list (and this check, which
  // uses the same non-fiction phase order) doesn't support fiction pieces —
  // leave their Review button behavior as it was before this fix.
  const hasCompletedAnyStage =
    isFictionArticle(piece) ||
    completedPhaseIndex(piece.generation_phases) !== -1;

  const next = suppressNextStep ? null : nextStepFor(piece.ui_state, piece.generation_phases);
  const canNext = !!next && !nextStepPending;

  // BUG-042 — founder-reported: clicking Next Step gave no feedback that the
  // click landed. Two of the three cases (Continue pipeline / Retry publish)
  // were previously fire-and-forget POST /run calls with no pending state,
  // no error surfaced, and status/ui_state never touched — the card wouldn't
  // even change columns. Now every path
  // patches status first (so the piece visibly moves to the right column —
  // In Progress or Scheduled, per next.status), waits for the pipeline-run
  // call too, then navigates back to the kanban board so landing there IS
  // the confirmation. A failure anywhere in the sequence keeps the founder
  // on the piece with the real error shown, instead of silently vanishing.
  const onNextStep = async () => {
    if (!next) return;
    setNextStepPending(true);
    setNextStepError(null);
    try {
      await update.mutateAsync({ action: "approve" });
      navigate("/kanban");
    } catch (err) {
      setNextStepError(describeRunError(err));
    } finally {
      setNextStepPending(false);
    }
  };

  const onDiscard = () => {
    update.mutate({ action: "reject" }, {
      onSuccess: () => setConfirmDiscard(false),
    });
  };

  const onSaveRevision = (target: string, notes: string) => {
    update.mutate(
      { action: "revise", revision_target: target, revision_notes: notes },
      {
        // BUG-105 — refused (e.g. the piece published while the modal was
        // open): the modal stays open with the notes, and the re-read piece
        // shows it as published so Send back disables itself.
        //
        // BUG-081 / BUG-104 still hold, now server-side: the decision resets
        // the piece to the target stage and enqueues the run, and the re-run
        // comes back for approval; it never publishes on its own. A refused
        // run (409/503) is not swallowed — it lands in update.error.
        onSuccess: () => setShowRevision(false),
        onError: (err) => {
          if (decisionLanded(err)) {
            setShowRevision(false);
            setNextStepError(describeRunError(err));
          }
        },
      }
    );
  };

  return (
    <>
      <div className="decision-bar">
        <div className="decision-bar-info">
          {next ? (
            <span className="decision-bar-hint">{next.hint}</span>
          ) : (
            <span className="decision-bar-hint decision-bar-hint-muted">
              No action available — piece is {labelFor(piece.ui_state)}
            </span>
          )}
        </div>
        {canDecide && (
          <div className="decision-group">
            <button
              className="btn-dec btn-approve"
              onClick={() => void onNextStep()}
              disabled={!canNext}
              title={next?.hint ?? "Nothing to advance"}
            >
              <Icon name="check" size={16} />{" "}
              {nextStepPending ? "Working…" : "Next Step"}
            </button>
            <button
              className="btn-dec btn-review"
              onClick={() => setShowSendForReview(true)}
              disabled={!draftHtml}
              title={draftHtml ? "Email the current draft for feedback" : "No draft yet"}
            >
              ✉ Send for feedback
            </button>
            <button
              className="btn-dec btn-review"
              onClick={() => setShowRevision(true)}
              disabled={
                update.isPending ||
                piece.ui_state === "rejected" ||
                piece.ui_state === "published" ||
                piece.ui_state === "fiction_published" ||
                !hasCompletedAnyStage
              }
              title={
                hasCompletedAnyStage
                  ? "Send back for revision"
                  : "Nothing has run yet — no stage to send back to"
              }
            >
              <Icon name="alert" size={16} /> Review
            </button>
            <button
              className="btn-dec btn-reject"
              onClick={() => setConfirmDiscard(true)}
              disabled={update.isPending || piece.ui_state === "rejected"}
              title="Discard piece"
            >
              <Icon name="x" size={16} /> Discard
            </button>
            {showStop && (
              <button
                className="btn-dec btn-review"
                onClick={() => setConfirmStop(true)}
                disabled={stop.isPending}
                title="Stop the run and reset the piece"
              >
                <Icon name="pause" size={16} /> Stop
              </button>
            )}
          </div>
        )}
      </div>

      {(nextStepError || update.error || stop.error) && (
        <div className="login-error" style={{ margin: "8px 24px" }}>
          {nextStepError ??
            (() => {
              if (update.error) return describeRunError(update.error);
              const err = stop.error;
              return err instanceof ApiError
                ? err.message
                : (err as Error).message;
            })()}
        </div>
      )}

      {showSendForReview && (
        <SendForReviewModal
          pieceId={piece.id}
          pieceTitle={piece.title}
          draftHtml={draftHtml}
          onCancel={() => setShowSendForReview(false)}
        />
      )}

      {showRevision && (
        <RevisionModal
          initialTarget={piece.revision_target ?? "writer"}
          initialNotes={piece.revision_notes ?? ""}
          generationPhases={piece.generation_phases}
          onCancel={() => setShowRevision(false)}
          onSave={onSaveRevision}
          saving={update.isPending}
          error={update.error ? describeRunError(update.error) : null}
        />
      )}

      {confirmDiscard && (
        <div
          className="rev-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmDiscard(false);
          }}
        >
          <div
            className="rev-modal"
            role="dialog"
            aria-labelledby="discard-title"
            style={{ maxWidth: 480 }}
          >
            <header className="rev-head">
              <div className="rev-head-l">
                <div className="rev-eyebrow">Confirm</div>
                <h2 id="discard-title" className="rev-title">
                  Discard this piece?
                </h2>
              </div>
              <button
                className="rev-x"
                onClick={() => setConfirmDiscard(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div className="rev-body">
              <p className="rev-lede">
                The piece will be marked <strong>rejected</strong> and removed
                from the kanban. Drafts, reports, and images stay in the
                database and are not deleted.
              </p>
            </div>

            <footer className="rev-foot">
              <div className="rev-foot-l" />
              <div className="rev-actions">
                <button
                  className="rev-btn rev-btn-ghost"
                  onClick={() => setConfirmDiscard(false)}
                  disabled={update.isPending}
                >
                  Cancel
                </button>
                <button
                  className="rev-btn rev-btn-danger"
                  onClick={onDiscard}
                  disabled={update.isPending}
                >
                  {update.isPending ? "Discarding…" : "Discard piece"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}

      {confirmStop && (
        <div
          className="rev-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmStop(false);
          }}
        >
          <div
            className="rev-modal"
            role="dialog"
            aria-labelledby="stop-title"
            style={{ maxWidth: 480 }}
          >
            <header className="rev-head">
              <div className="rev-head-l">
                <div className="rev-eyebrow">Confirm</div>
                <h2 id="stop-title" className="rev-title">
                  Stop this run?
                </h2>
              </div>
              <button
                className="rev-x"
                onClick={() => setConfirmStop(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>

            <div className="rev-body">
              <p className="rev-lede">
                Any in-progress AI work will be discarded and the piece returns
                to <strong>Your Turn</strong>. Drafts, reports, and images are
                kept — you can re-launch the pipeline afterward.
              </p>
            </div>

            <footer className="rev-foot">
              <div className="rev-foot-l" />
              <div className="rev-actions">
                <button
                  className="rev-btn rev-btn-ghost"
                  onClick={() => setConfirmStop(false)}
                  disabled={stop.isPending}
                >
                  Cancel
                </button>
                <button
                  className="rev-btn rev-btn-danger"
                  onClick={() => stop.mutate()}
                  disabled={stop.isPending}
                >
                  {stop.isPending ? "Stopping…" : "Stop run"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function labelFor(ui: UiState | null | undefined): string {
  if (!ui) return "in an unknown state";
  if (ui === "rejected") return "discarded";
  if (ui === "published" || ui === "fiction_published") return "published";
  if (ui === "scheduled") return "scheduled";
  if (ui.startsWith("ai_")) return "still being processed by the AI";
  if (ui === "idea") return "an idea";
  return ui.replace(/_/g, " ");
}
